import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SelectNativo } from '@/components/ui/select-nativo';
import { cn } from '@/lib/utils';
import { DISPAROS } from '../../lib/corridas';
import type { RangoDeFechas } from '../../lib/rango-de-fechas';
import type { Disparo } from '../../types/corridas.types';
import { SelectorDeFechas } from '../selector-de-fechas';

export interface FiltroDeCorridas {
  estrategia: string | null;
  periodo: RangoDeFechas;
  disparos: Disparo[];
}

/**
 * Estrategia (sólo en «Por fechas»), Período (el selector de fechas de Prixia) y
 * «Qué la lanzó» con su ayuda (maqueta: `filterBar`).
 */
export function FiltrosDeCorridas({
  valor,
  onCambiar,
  estrategias,
}: {
  valor: FiltroDeCorridas;
  onCambiar: (v: FiltroDeCorridas) => void;
  /** Sin esto no se ofrece el selector de estrategia (la vista de una estrategia). */
  estrategias?: Array<{ id: string; nombre: string }>;
}) {
  const alternar = (d: Disparo) =>
    onCambiar({
      ...valor,
      disparos: valor.disparos.includes(d)
        ? valor.disparos.filter((x) => x !== d)
        : [...valor.disparos, d],
    });
  return (
    <div
      data-testid="filtros-de-corridas"
      className="flex flex-wrap items-end gap-x-5 gap-y-3 rounded-tarjeta border bg-card shadow-sm p-3"
    >
      {estrategias && (
        <label className="flex min-w-0 max-w-full flex-col gap-1 text-xs text-muted-foreground">
          Estrategia
          <SelectNativo
            aria-label="Estrategia"
            className="h-9 w-72 max-w-full"
            // Revisión final de rama (F6): un nombre largo se corta; el title lo
            // dice entero (como la bandeja).
            title={
              estrategias.find((e) => e.id === valor.estrategia)?.nombre ?? 'Todas las estrategias'
            }
            value={valor.estrategia ?? ''}
            onChange={(ev) => onCambiar({ ...valor, estrategia: ev.target.value || null })}
          >
            <option value="">Todas las estrategias</option>
            {estrategias.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}
              </option>
            ))}
          </SelectNativo>
        </label>
      )}
      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
        Período
        <SelectorDeFechas
          etiqueta="Período"
          valor={valor.periodo}
          onCambiar={(periodo) => onCambiar({ ...valor, periodo })}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          Qué la lanzó
          <Popover>
            <PopoverTrigger
              aria-label="Qué significa cada opción"
              className="inline-grid h-4 w-4 place-items-center rounded-full border text-xs font-bold leading-none text-muted-foreground"
            >
              ?
            </PopoverTrigger>
            <PopoverContent className="w-96 max-w-[calc(100vw-2rem)] space-y-1.5 text-xs">
              {DISPAROS.map((d) => (
                <p key={d.clave} className="[overflow-wrap:anywhere]">
                  <b>{d.texto}</b>: {d.ayuda}
                </p>
              ))}
            </PopoverContent>
          </Popover>
        </span>
        <div role="group" aria-label="Qué la lanzó" className="flex flex-wrap gap-1.5">
          {DISPAROS.map((d) => {
            const prendido = valor.disparos.includes(d.clave);
            return (
              <button
                key={d.clave}
                type="button"
                aria-pressed={prendido}
                title={`${d.texto}: ${d.ayuda}`}
                onClick={() => alternar(d.clave)}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs',
                  prendido
                    ? 'border-primary bg-primary/10 font-semibold text-primary'
                    : 'text-muted-foreground'
                )}
              >
                {d.texto}
              </button>
            );
          })}
        </div>
      </div>
      <p className="ml-auto text-xs text-muted-foreground">Fechas y horas en hora de Bolivia</p>
    </div>
  );
}
